import { useMemo, useState } from 'react';
import type { CalendarItem } from '@st/contract';
import {
  useCalendar,
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  usePlanWorkout,
  useSetParticipants,
  useTrainerClients,
  useTrainingPlan,
  useUpdateCalendarEvent,
} from '../../api/queries';
import { useAuth } from '../../auth/AuthProvider';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDate, formatTime, todayIso } from '../../lib/format';
import { planWorkouts } from '../../lib/program';
import {
  PLANNED_WORKOUT_TYPE,
  fromStartsAt,
  gridRange,
  isPlannedWorkout,
  itemState,
  monthGrid,
  monthStart,
  shiftMonth,
  type CalendarItemState,
} from '../../lib/calendarGrid';
import type { PlannedEventItem } from '../../api/types';
import {
  DEFAULT_REMINDER_MINUTES,
  PlanWorkoutModal,
  type PlanWorkoutValues,
} from './PlanWorkoutModal';
import { cx } from '../../lib/cx';
import {
  Badge,
  Button,
  CalendarMonth,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  ParticipantPicker,
  Select,
  SkeletonCard,
  Textarea,
  useToast,
  type BadgeTone,
  type ParticipantPickerLabels,
} from '../../ui';
import {
  IconCalendarPlus,
  IconChevronLeft,
  IconChevronRight,
} from '../../components/icons';

const STATE_TONE: Record<CalendarItemState, BadgeTone> = {
  done: 'success',
  missed: 'danger',
  upcoming: 'neutral',
  event: 'info',
  planned: 'accent',
};

const STATE_KEY: Record<CalendarItemState, string> = {
  done: 'cal.done',
  missed: 'cal.missed',
  upcoming: 'cal.upcoming',
  event: 'cal.event',
  planned: 'cal.planned',
};

const LEGEND_STATES = ['done', 'missed', 'upcoming', 'event', 'planned'] as const;

const EVENT_TYPES = ['appointment', 'check_in', 'measurement', 'rest'] as const;
const EVENT_TYPE_KEY: Record<(typeof EVENT_TYPES)[number], string> = {
  appointment: 'cal.typeAppointment',
  check_in: 'cal.typeCheckIn',
  measurement: 'cal.typeMeasurement',
  rest: 'cal.typeRest',
};

const WEEKDAY_KEYS = ['pon', 'uto', 'sri', 'čet', 'pet', 'sub', 'ned'] as const;

/**
 * Mjesečni kalendar. Spaja tri izvora:
 *  - propisane treninge (`kind:'workout'`, neizmjenjivi — mijenja ih program),
 *  - termine koje zakazuje trener (`kind:'event'`),
 *  - termine koje klijent zakaže sam (`type: 'planned_workout'`, s podsjetnikom).
 * Odabrani dan živi u state-u jer je to prikaz istog podatka, ne druga ruta.
 */
export function Calendar() {
  const { t, locale } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();

  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';
  const today = todayIso();

  const [month, setMonth] = useState(() => monthStart(today));
  const [selectedDay, setSelectedDay] = useState<string>(today);
  const [clientId, setClientId] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CalendarItem | null>(null);

  /** Sudionici novog termina; prazan popis znači „još nije dirano”. */
  const [participants, setParticipants] = useState<string[]>([]);
  const [participantError, setParticipantError] = useState<string | undefined>(undefined);
  /** Grupa čiji se popis sudionika uređuje; `null` znači da modal nije otvoren. */
  const [editGroup, setEditGroup] = useState<{ groupId: string; clientIds: string[] } | null>(null);

  const [eventType, setEventType] = useState<(typeof EVENT_TYPES)[number]>('appointment');
  const [title, setTitle] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [startError, setStartError] = useState<string | undefined>(undefined);

  /** Termin koji klijent zakazuje sam; `null` znači da modal nije otvoren. */
  const [planOpen, setPlanOpen] = useState(false);
  const [planEdit, setPlanEdit] = useState<PlannedEventItem | null>(null);

  const range = useMemo(() => gridRange(month), [month]);
  const clients = useTrainerClients();
  /*
    Izbornik namjerno kreće prazan, s pozivom „Odaberi klijenta". Prije je pisao ime
    prvog klijenta, a upit je išao bez `clientId` — trener je gledao svoj prazan
    kalendar i mislio da termini nestaju. Prikaz i upit sada čitaju istu vrijednost,
    a dok nitko nije odabran kalendar se i ne dohvaća.
  */
  const effectiveClientId = isTrainer ? (clientId || undefined) : undefined;
  const trebaOdabir = isTrainer && !effectiveClientId;
  const calendar = useCalendar(range.from, range.to, effectiveClientId, !trebaOdabir);
  const createEvent = useCreateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();
  const planWorkout = usePlanWorkout();
  const updateEvent = useUpdateCalendarEvent();
  const saveParticipants = useSetParticipants();
  // Klijent smije vezati termin uz propisani trening; trener ovu listu ne treba.
  const plan = useTrainingPlan(!isTrainer);

  const weekdayLabels = useMemo(() => {
    const base = new Date(Date.UTC(2024, 0, 1)); // ponedjeljak
    return WEEKDAY_KEYS.map((_, i) => {
      const d = new Date(base);
      d.setUTCDate(d.getUTCDate() + i);
      return new Intl.DateTimeFormat(locale === 'hr' ? 'hr-HR' : 'en-GB', {
        weekday: 'short',
      }).format(d);
    });
  }, [locale]);

  const monthLabel = new Intl.DateTimeFormat(locale === 'hr' ? 'hr-HR' : 'en-GB', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${month}T00:00:00Z`));

  const participantLabels: ParticipantPickerLabels = {
    search: t('cal.participantsSearch'),
    selected: t('cal.participantsSelected', { n: '{n}' }),
    all: t('cal.participantsAll'),
    none: t('cal.participantsNone'),
    empty: t('cal.participantsEmpty'),
    noMatch: t('cal.participantsNoMatch'),
  };

  const participantOptions = (clients.data?.items ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    hint: c.email,
  }));

  /**
   * Termin se otvara s klijentom kojeg trener trenutačno gleda već označenim.
   * Ako nitko nije odabran, ne pogađamo — popis ostaje prazan.
   */
  function openCreate(): void {
    setParticipants(effectiveClientId ? [effectiveClientId] : []);
    setParticipantError(undefined);
    setStartsAt(`${selectedDay}T18:00`);
    setCreateOpen(true);
  }

  async function onCreate(): Promise<void> {
    if (participants.length === 0) {
      setParticipantError(t('cal.participantsRequired'));
      return;
    }
    if (!startsAt) {
      setStartError(t('cal.startRequired'));
      return;
    }
    try {
      const ishod = await createEvent.mutateAsync({
        clientIds: participants,
        type: eventType,
        startsAt: new Date(startsAt).toISOString(),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(location.trim() ? { location: location.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setCreateOpen(false);
      setTitle('');
      setStartsAt('');
      setLocation('');
      setNote('');
      setStartError(undefined);
      setParticipantError(undefined);
      toast(
        ishod.count > 1 ? t('cal.createdGroup', { n: ishod.count }) : t('cal.created'),
        'success',
      );
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function onSaveParticipants(): Promise<void> {
    if (!editGroup) return;
    if (editGroup.clientIds.length === 0) {
      setParticipantError(t('cal.participantsRequired'));
      return;
    }
    try {
      await saveParticipants.mutateAsync({
        groupId: editGroup.groupId,
        clientIds: editGroup.clientIds,
      });
      setEditGroup(null);
      setParticipantError(undefined);
      toast(t('cal.participantsSaved'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  /**
   * Zakazivanje i premještanje idu na dvije različite rute: novi termin nastaje
   * kroz `plan-workout` (server tada postavlja zadani podsjetnik), a premještanje
   * kroz `updateEvent`, koji podsjetnik vraća u red.
   */
  async function onPlan(values: PlanWorkoutValues): Promise<void> {
    try {
      if (planEdit) {
        await updateEvent.mutateAsync({
          id: planEdit.id,
          patch: {
            startsAt: values.startsAt,
            remindMinutesBefore: values.remindMinutesBefore,
          },
        });
        toast(t('cal.planMoved'), 'success');
      } else {
        await planWorkout.mutateAsync({
          startsAt: values.startsAt,
          workoutId: values.workoutId,
          title: values.title,
          remindMinutesBefore: values.remindMinutesBefore,
        });
        toast(t('cal.planCreated'), 'success');
      }
      setPlanOpen(false);
      setPlanEdit(null);
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function onDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteEvent.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
      toast(t('cal.deleted'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <>
      <PageHeader
        title={t('cal.title')}
        action={
          isTrainer ? (
            <Button size="sm" onClick={openCreate}>
              {t('cal.newEvent')}
            </Button>
          ) : (
            <Button
              size="sm"
              leading={<IconCalendarPlus width={16} height={16} />}
              onClick={() => {
                setPlanEdit(null);
                setPlanOpen(true);
              }}
            >
              {t('cal.planWorkout')}
            </Button>
          )
        }
      />

      {isTrainer && (
        <div className="mb-5 max-w-sm">
          <QueryBoundary query={clients} skeleton={<SkeletonCard />}>
            {(data) => (
              <Select
                id="cal-client"
                label={t('cal.client')}
                placeholder={t('cal.selectClient')}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                options={data.items.map((c) => ({ value: c.id, label: c.name }))}
              />
            )}
          </QueryBoundary>
        </div>
      )}

      {trebaOdabir ? (
        <EmptyState
          title={t('cal.selectClient')}
          message={t('cal.selectClientHelp')}
          icon={<IconCalendarPlus width={28} height={28} />}
        />
      ) : (
      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Card>
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display text-[18px] font-semibold capitalize text-ink">
              {monthLabel}
            </h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label={t('cal.prev')}
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                className="rounded-full p-2 text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
              >
                <IconChevronLeft width={18} height={18} />
              </button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setMonth(monthStart(today));
                  setSelectedDay(today);
                }}
              >
                {t('cal.today')}
              </Button>
              <button
                type="button"
                aria-label={t('cal.next')}
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                className="rounded-full p-2 text-ink-muted transition-colors hover:bg-bg-alt hover:text-ink"
              >
                <IconChevronRight width={18} height={18} />
              </button>
            </div>
          </div>

          <QueryBoundary query={calendar} skeleton={<SkeletonCard />}>
            {(data) => {
              const days = monthGrid(month, data.items, today);
              return (
                <>
                  <CalendarMonth
                    days={days}
                    weekdayLabels={weekdayLabels}
                    selectedDay={selectedDay}
                    onSelectDay={setSelectedDay}
                    formatDayNumber={(d) => String(Number(d.slice(8, 10)))}
                    dayAriaLabel={(d) =>
                      t('cal.dayLabel', {
                        date: formatDate(`${d.day}T00:00:00.000Z`, locale),
                        n: d.items.length,
                      })
                    }
                  />

                  <ul className="mt-4 flex flex-wrap gap-3" aria-label={t('cal.legend')}>
                    {LEGEND_STATES.map((state) => (
                      <li key={state} className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                        <span
                          aria-hidden="true"
                          className={cx(
                            'block h-2 w-2',
                            state === 'event' || state === 'planned'
                              ? 'rounded-[2px]'
                              : 'rounded-full',
                            state === 'event' && 'bg-info',
                            state === 'planned' && 'border-2 border-accent',
                            state === 'done' && 'bg-success',
                            state === 'missed' && 'border-2 border-danger',
                            state === 'upcoming' && 'bg-accent',
                          )}
                        />
                        {t(STATE_KEY[state])}
                      </li>
                    ))}
                  </ul>
                </>
              );
            }}
          </QueryBoundary>
        </Card>

        <Card>
          <CardHeader title={formatDate(`${selectedDay}T00:00:00.000Z`, locale)} />
          <QueryBoundary query={calendar} skeleton={<SkeletonCard />}>
            {(data) => {
              const items = data.items.filter((i) => i.day === selectedDay);
              if (items.length === 0) {
                return <p className="text-[14px] text-ink-muted">{t('cal.dayEmpty')}</p>;
              }
              return (
                <ul className="flex flex-col gap-3">
                  {items.map((item) => {
                    const state = itemState(item);
                    const planned = isPlannedWorkout(item);
                    return (
                      <li
                        key={`${item.kind}-${item.id}`}
                        data-testid={planned ? 'planned-event' : undefined}
                        className={cx(
                          'rounded-md p-3',
                          planned
                            ? 'border-2 border-dashed border-accent bg-accent-light/40'
                            : 'border border-line-subtle',
                        )}
                      >
                        <div className="mb-1 flex items-start justify-between gap-2">
                          <span className="text-[14.5px] text-ink">
                            {planned && item.title === PLANNED_WORKOUT_TYPE
                              ? t('cal.planWorkout')
                              : item.title}
                          </span>
                          <Badge tone={STATE_TONE[state]}>{t(STATE_KEY[state])}</Badge>
                        </div>
                        {item.kind === 'event' ? (
                          <>
                            <p className="text-[12.5px] text-ink-muted">
                              {formatTime(item.startsAt, locale)}
                              {item.location ? ` · ${item.location}` : ''}
                              {item.groupId
                                ? ` · ${t('cal.groupCount', { n: item.participantCount })}`
                                : ''}
                            </p>
                            {/* Imena stižu samo treneru; klijent u odgovoru dobiva broj. */}
                            {item.participants && item.participants.length > 0 && (
                              <p className="mt-1 text-[12.5px] text-ink">
                                {item.participants.map((sudionik) => sudionik.name).join(', ')}
                              </p>
                            )}
                            {planned && (
                              <p className="mt-1 text-[12.5px] text-ink-muted">
                                {t('cal.plannedByYou')}
                                {reminderLabel(item as PlannedEventItem, t)}
                              </p>
                            )}
                            {item.note && (
                              <p className="mt-1 text-[12.5px] text-ink-muted">{item.note}</p>
                            )}
                            <div className="mt-2 flex justify-end gap-2">
                              {isTrainer && item.groupId && item.participants && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => {
                                    setEditGroup({
                                      groupId: item.groupId as string,
                                      clientIds: (item.participants ?? []).map((x) => x.clientId),
                                    });
                                    setParticipantError(undefined);
                                  }}
                                >
                                  {t('cal.participantsEdit')}
                                </Button>
                              )}
                              {planned && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => {
                                    setPlanEdit(item as PlannedEventItem);
                                    setPlanOpen(true);
                                  }}
                                >
                                  {t('cal.reschedule')}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPendingDelete(item)}
                              >
                                {t('cal.deleteEvent')}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <p className="text-[12.5px] text-ink-muted">
                            {t('cal.prescribed')} · {t('cal.notEditable')}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            }}
          </QueryBoundary>
        </Card>
      </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('cal.newEvent')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={createEvent.isPending} onClick={() => void onCreate()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] text-ink-muted">{t('cal.participants')}</span>
            <ParticipantPicker
              id="event-participants"
              options={participantOptions}
              value={participants}
              onChange={(ids) => {
                setParticipants(ids);
                setParticipantError(undefined);
              }}
              labels={participantLabels}
              error={participantError}
            />
            {participants.length > 1 && (
              <p className="text-[12px] text-ink-muted">{t('cal.groupHint')}</p>
            )}
          </div>
          <Select
            id="event-type"
            label={t('cal.eventType')}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as (typeof EVENT_TYPES)[number])}
            options={EVENT_TYPES.map((value) => ({ value, label: t(EVENT_TYPE_KEY[value]) }))}
          />
          <Input
            id="event-title"
            label={t('cal.eventTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            id="event-start"
            type="datetime-local"
            label={t('cal.startsAt')}
            value={startsAt}
            error={startError}
            onChange={(e) => {
              setStartsAt(e.target.value);
              setStartError(undefined);
            }}
          />
          <Input
            id="event-location"
            label={t('cal.location')}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          <Textarea
            id="event-note"
            label={t('cal.note')}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </Modal>

      {/* `key` vraća modal na početne vrijednosti kad se mijenja termin koji uređujemo. */}
      {planOpen && (
        <PlanWorkoutModal
          key={planEdit?.id ?? 'new'}
          open={planOpen}
          onClose={() => {
            setPlanOpen(false);
            setPlanEdit(null);
          }}
          mode={planEdit ? 'edit' : 'create'}
          initialDay={planEdit ? fromStartsAt(planEdit.startsAt).day : selectedDay}
          {...(planEdit ? { initialTime: fromStartsAt(planEdit.startsAt).time } : {})}
          initialReminder={
            planEdit ? (planEdit.remindMinutesBefore ?? null) : DEFAULT_REMINDER_MINUTES
          }
          workouts={planWorkouts(plan.data).map((w) => ({ id: w.workoutId, title: w.name }))}
          loading={planWorkout.isPending || updateEvent.isPending}
          onSubmit={(values) => void onPlan(values)}
        />
      )}

      <Modal
        open={editGroup !== null}
        onClose={() => setEditGroup(null)}
        title={t('cal.participantsEdit')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditGroup(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={saveParticipants.isPending}
              onClick={() => void onSaveParticipants()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {editGroup && (
          <ParticipantPicker
            id="group-participants"
            options={participantOptions}
            value={editGroup.clientIds}
            onChange={(ids) => {
              setEditGroup({ ...editGroup, clientIds: ids });
              setParticipantError(undefined);
            }}
            labels={participantLabels}
            error={participantError}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('cal.deleteEvent')}
        question={
          /* Kod grupne aktivnosti trener mora znati da briše termin svima. */
          pendingDelete && pendingDelete.kind === 'event' && pendingDelete.groupId && isTrainer
            ? t('cal.groupDeleteQuestion', {
                title: pendingDelete.title,
                n: pendingDelete.participantCount,
              })
            : t('cal.deleteQuestion', { title: pendingDelete?.title ?? '' })
        }
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={deleteEvent.isPending}
        onConfirm={() => void onDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </>
  );
}

/** Kratka napomena o podsjetniku uz zakazani termin. */
function reminderLabel(
  item: PlannedEventItem,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  const minutes = item.remindMinutesBefore;
  if (minutes === null || minutes === undefined) return ` · ${t('cal.reminderNone')}`;
  return ` · ${t('cal.reminderMinutes', { n: minutes })}`;
}
